<template>
  <div class="grow">
    <h3 class="sans-serif font-weight-bold primary--text mb-4">
      {{ this.title || $t('analysis.keyFindings.header') }}
    </h3>

    <v-row
      v-if="reportContextButtonKey || aiSummary"
      no-gutters
      justify="end"
      class="pb-4"
    >
      <v-col class="shrink">
        <platform-button
          v-if="aiSummary || aiKeyFindings"
          secondary
          default-case
          icon="robot-outline"
          class="mr-2"
          @click="showAiConfirmDialog = true"
        >
          {{ $t('analysis.ai.insertContentAction') }}
        </platform-button>
      </v-col>
      <v-col class="shrink">
        <report-context-button
          v-if="reportContextButtonKey"
          :value="reportContextButtonKey"
          :result="result"
          :report-result="reportResult"
          :section="section"
          :master-section-id="reportResult.masterSectionId"
          :master-block-id="reportResult.masterBlockId"
          :criteria-id="reportResult.criteriaId"
          @insertRecommendation="handleInsertRecommendedSummaryContent"
        />
      </v-col>
    </v-row>

    <platform-text-area
      outlined
      auto-grow
      :rows="4"
      :label="$t('analysis.summary.title')"
      :rules="form.validation.summary"
      :value="answer.summary"
      ref="summary"
      justified
      @input="(val) => handleSummaryInput(val)"
    >
      <template #append>
        <platform-status-indicator
          v-if="answer.summary || answer.values"
          icon-only
          :warning-tooltip="$t('platformStatusIndicator.warningTooltip')"
          :loading="form.savingSummary"
          :saved="form.savedSummary"
          :warning="$refs.summary ? !$refs.summary.valid : false"
        />
      </template>
    </platform-text-area>

    <v-row
      no-gutters
      v-for="(item, i) in fields"
      :key="i"
      class="pt-2"
    >
      <platform-text-area
        auto-grow
        outlined
        ref="findings"
        :value="answer.values[i]"
        :rows="1"
        :label="`${$t('analysis.keyFindings.text', [i + 1])}`"
        :rules="form.validation.value"
        justified
        @input="(val) => handleInput(i, val)"
      >
        <template #append>
          <platform-status-indicator
            v-if="answer.values || answer.summary"
            icon-only
            :warning-tooltip="$t('platformStatusIndicator.warningTooltip')"
            :loading="form.savingValues.includes(i)"
            :saved="form.savedValues.includes(i)"
            :warning="$refs.findings && $refs.findings[i] ? !$refs.findings[i].valid : false"
          />
        </template>
      </platform-text-area>
    </v-row>

    <!-- AI Summary Confirmation Dialog -->
    <platform-modal
      v-model="showAiConfirmDialog"
      :title="$t('analysis.ai.useAiContentTitle')"
      :width="600"
      icon="robot-outline"
    >
      <p>{{ $t('analysis.ai.replaceKeyFindingsInstruction') }}</p>

      <div v-if="aiSummary" class="mt-3">
        <p class="text-subtitle-2 mb-2">
          <strong>{{ $t('analysis.summary.title') }}:</strong>
        </p>
        <div
          class="grey lighten-4 rounded pa-3 pr-5 overflow-y-auto"
          :style="{ maxHeight: '300px' }"
        >
          <p class="text-body-2 mb-0 text-justify">{{ aiSummary }}</p>
        </div>
      </div>

      <div v-if="aiKeyFindings && aiKeyFindings.length" class="mt-3">
        <p class="text-subtitle-2 mb-2">
          <strong>{{ $t('analysis.keyFindings.header') }}:</strong>
        </p>
        <div
          class="grey lighten-4 rounded pa-3 pr-5 overflow-y-auto"
          :style="{ maxHeight: '300px' }"
        >
          <ol class="text-body-2 mb-0 text-justify">
            <li v-for="(finding, index) in aiKeyFindings" :key="index" class="mb-1">
              {{ finding }}
            </li>
          </ol>
        </div>
      </div>

      <p class="mt-3 text-caption warning--text">
        {{ $t('analysis.ai.replaceWarning') }}
      </p>
      <template #actions>
        <platform-button
          primary
          @click="useAiSummary"
        >
          {{ $t('analysis.ai.replaceWithContentAction') }}
        </platform-button>
      </template>
    </platform-modal>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import { debounce } from 'lodash'
import { mapActions } from 'vuex'
import { CommitteeSummaryField, ResponsesResultKeyFindings } from '@/types/Report'
import { MaxFieldLengths, Validators } from '@/helpers/validation'
import PlatformTextArea from '@/components/shared/inputs/PlatformTextArea.vue'
import PlatformStatusIndicator from '@/components/shared/PlatformStatusIndicator.vue'
import PlatformButton from '@/components/shared/PlatformButton.vue'
import PlatformModal from '@/components/PlatformModal.vue'
import { ReportField } from '@betterboards/shared/helpers/entities/Report/index'
import { PlatformSection } from '@betterboards/shared/types/Platform'
import ReportContextButton from '@/components/analysis/ReportContextButton.vue'
import { ReportContextItem } from '@/helpers/recommendations'
import { ResponsesResult } from '@betterboards/shared/types/ResultSet'

export default Vue.extend({
  name: 'KeyFindingAnswer',
  components: {
    ReportContextButton,
    PlatformStatusIndicator,
    PlatformTextArea,
    PlatformButton,
    PlatformModal
  },
  props: {
    result: Object as PropType<ResponsesResult>,
    reportResult: Object as PropType<ResponsesResultKeyFindings | CommitteeSummaryField>,
    section: { type: Object as PropType<PlatformSection>, required: true },
    fields: { type: Number, default: 5 },
    title: { type: String, required: false }
  },
  data () {
    return {
      answer: <ResponsesResultKeyFindings | CommitteeSummaryField>{
        field: undefined,
        questionId: undefined,
        criteriaId: undefined,
        groupId: undefined,
        summary: <string | undefined>undefined,
        values: <Array<string | undefined>>[]
      },
      form: {
        validation: {
          summary: [Validators.maxLength(MaxFieldLengths.KeyFindingsSummary)],
          value: [Validators.maxLength(MaxFieldLengths.KeyFindingsValue)]
        },
        saving: false,
        saved: false,
        savingSummary: false,
        savedSummary: false,
        savingValues: <number[]>[],
        savedValues: <number[]>[]
      },
      updateResult: <undefined | (() => void)>undefined,
      MaxFieldLengths,
      showAiConfirmDialog: false
    }
  },
  mounted () {
    this.init()
    this.updateResult = debounce(this._updateResult, 1500, {
      leading: false,
      trailing: true
    })
  },
  watch: {
    reportResult (): void {
      this.form.saved = true
      this.form.saving = false
      this.init()
      this.setSavedStatus()
    }
  },
  computed: {
    aiContent (): any {
      return this.$store.state.Analysis.aiContent
    },
    reportContextButtonKey (): ReportContextItem | undefined {
      if (this.isCommitteeStructureResult) {
        return ReportContextItem.CommitteesStructurePage
      }
      return ReportContextItem.KeyFindings
    },
    isCommitteeStructureResult (): boolean {
      return this.reportResult.field === ReportField.CommitteeSummary
    },
    aiSummary (): string | undefined {
      if (!this.aiContent?.groupByQuestion || !this.reportResult) {
        return undefined
      }

      // Create the matching key for AI data
      const masterSectionId = this.reportResult.masterSectionId
      const masterBlockId = this.reportResult.masterBlockId
      const repeatForCriteriaId = this.reportResult.criteriaId || 'default'

      if (!masterSectionId || !masterBlockId) {
        return undefined
      }

      const aiKey = `${masterSectionId}-${masterBlockId}-${repeatForCriteriaId}`
      const aiData = this.aiContent.groupByQuestion[aiKey]

      return aiData?.summariser
    },
    aiKeyFindings (): string[] | undefined {
      if (!this.aiContent?.groupByQuestion || !this.reportResult) {
        return undefined
      }

      // Create the matching key for AI data
      const masterSectionId = this.reportResult.masterSectionId
      const masterBlockId = this.reportResult.masterBlockId
      const repeatForCriteriaId = this.reportResult.criteriaId || 'default'

      if (!masterSectionId || !masterBlockId) {
        return undefined
      }

      const aiKey = `${masterSectionId}-${masterBlockId}-${repeatForCriteriaId}`
      const aiData = this.aiContent.groupByQuestion[aiKey]

      // Extract topics from topicClustering
      return aiData?.topicClustering?.topics?.map((topic: any) => topic.topic)
    }
  },
  methods: {
    ...mapActions('Report', ['saveReportSection']),
    init (): void {
      if (!this.reportResult) {
        return
      }
      if (!this.reportResult.questionId && !this.reportResult.field) {
        console.error('Failed to init KeyFindingAnswer, ReportResult missing questionId or field:', this.reportResult)
        throw new Error('Key Finding Answer missing key fields.')
      }
      this.answer.field = this.reportResult.field
      this.answer.questionId = this.reportResult.questionId
      this.answer.criteriaId = this.reportResult.criteriaId
      this.answer.groupId = this.reportResult.groupId
      if (this.reportResult.summary && !this.answer.summary) {
        this.answer.summary = this.reportResult.summary
      }
      if (!this.reportResult.values) {
        return
      }
      if (!this.answer.values) {
        this.answer.values = []
      }
      if (this.answer.values.length) {
        return
      }
      this.answer.values.splice(0, this.answer.values.length, ...this.reportResult.values)
    },
    handleSummaryInput (val: string) {
      if (!this.updateResult) {
        return
      }
      const summary: any = this.$refs.summary
      if (!summary || !summary.validate()) {
        return
      }
      this.answer.summary = val
      this.form.saved = false
      this.form.saving = true
      this.form.savedSummary = false
      this.form.savingSummary = true
      this.updateResult()
    },
    handleInput (index: number, val: string) {
      if (!this.updateResult) {
        return
      }
      const input: any = this.$refs.findings?.[index]
      const valid = input?.validate()
      if (!input || !valid) {
        return
      }
      if (!this.answer.values) {
        this.answer.values = []
      }
      if (this.answer.values.length < index) {
        for (let i = this.answer.values.length; i < index; i++) {
          this.answer.values.push('')
        }
      }
      this.answer.values.splice(index, 1, val)
      this.form.saved = false
      this.form.saving = true

      this.setValueSavingStatus(index)
      this.updateResult()
    },
    _updateResult (): void {
      this.$emit('update', this.answer)
    },
    handleInsertRecommendedSummaryContent (content: string): void {
      const newContent = this.answer.summary
        ? `${this.answer.summary}\n\n${content}`
        : content
      this.handleSummaryInput(newContent)
    },
    useAiSummary (): void {
      // Apply AI summary if available
      if (this.aiSummary) {
        // For textarea, we can't use HTML tags, so just insert the plain text
        this.handleSummaryInput(this.aiSummary)
      }

      // Apply AI key findings if available
      if (this.aiKeyFindings && this.aiKeyFindings.length) {
        // Ensure we have enough empty values in the array
        const updatedValues = [...(this.answer.values || [])]

        // Fill with AI key findings (up to the number of fields)
        this.aiKeyFindings.slice(0, this.fields).forEach((finding, index) => {
          if (index < this.fields) {
            // For textarea, we can't use HTML tags, so just insert the plain text
            updatedValues[index] = finding
          }
        })

        // Update all the key findings at once
        this.answer.values = updatedValues
        this._updateResult()
      }

      this.showAiConfirmDialog = false
      this.$toasted.success(this.$t('analysis.aiAppliedToast') as string)
    },
    setSavedStatus (): void {
      if (!this.form.savedSummary) {
        this.form.savedSummary = this.form.savingSummary
      }

      this.form.savingValues.forEach(this.setValueSavedStatus)

      this.form.savingSummary = false
      this.form.savingValues.splice(0, this.form.savedValues.length)
    },
    setValueSavedStatus (index: number): void {
      const savedExistsIndex: number = this.form.savedValues.indexOf(index)
      if (savedExistsIndex === -1) {
        this.form.savedValues.push(index)
      }
    },
    setValueSavingStatus (index: number): void {
      const savedExistsIndex: number = this.form.savedValues.indexOf(index)
      if (savedExistsIndex !== -1) {
        this.form.savedValues.splice(savedExistsIndex, 1)
      }

      const savingExistsIndex: number = this.form.savingValues.indexOf(index)
      if (savingExistsIndex === -1) {
        this.form.savingValues.push(index)
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.text-justify {
  text-align: justify;
}
</style>
